import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useState, useCallback } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';

import * as graphql from 'generated/graphql';
import { Link, useHistory } from '@treams/treams-routes';
import { Popover, Subheading, Paragraph, Button, IconButton, Checkbox, Label, Tabs, Select } from '@treams/treams-ui';
import useLanguage from 'packages/kakapo-ui-application/hooks/useLanguage';
import { MagicChatModal } from 'goals/goal-magic/MagicChatModal';
import { ChevronDownIcon } from '@treams/treams-icons';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { rgba } from 'polished';
import Delta from 'quill-delta';

const CompetencySuggestionContainer = styled(Box)`
	background: ${(props) => props.theme.colors.white};
	border-radius: 8px;
	padding: 15px 18px;
`;

const CollapseButton = styled(Box)<{ collapsed: boolean; }>`
	transition: transform 0.2s;
	transform: rotate(${(props) => (props.collapsed ? -90 : 0)}deg);
`;

const Collapsable = styled(Box)<{ collapsed: boolean; }>`
	transition: max-height 0.2s, padding-top 0.2s;
	max-height: ${(props) => (props.collapsed ? 0 : 300)}px;
	padding-top: ${(props) => (props.collapsed ? 0 : 8)}px;
	box-sizing: border-box;
	overflow: hidden;
`;

const LevelCriteria = styled(Paragraph)`
	background: ${(props) => rgba(props.theme.colors.lightBlue, 0.3)};
	border-radius: 4px;
	padding: 8px 12px;
`;

function CompetencySuggestion(props: {
	data: graphql.CompetencySuggestion;
	levelSet: graphql.CompetencyLevelSet;
	selected: boolean;
	onToggleSelected: () => void;
}) {
	const { localize } = useLocalize();
	const [collapsed, setCollapsed] = useState(true);
	const [selectedLevel, setSelectedLevel] = useState(0);

	return (
		<>
			<CompetencySuggestionContainer mb={2}>
				<Flex alignItems="center">
					<CollapseButton collapsed={collapsed} ml={-2} mt={-1} mr={1} flexShrink={0}>
						<IconButton icon={<ChevronDownIcon />} type="plain" onClick={() => setCollapsed(!collapsed)} />
					</CollapseButton>
					<Subheading mb={1} flexGrow={1}>{props.data.title}</Subheading>
					<Checkbox ml={3} checked={props.selected} onClick={props.onToggleSelected} flexShrink={0} />
				</Flex>
				<Collapsable collapsed={collapsed}>
					<Paragraph>{props.data.description}</Paragraph>
					<Tabs.Inline mt={3}>
						{props.data.levels.map((criteria, idx) => (
							<Tabs.Inline.Item key={idx} py={2} px={2} selected={selectedLevel === idx} onClick={() => setSelectedLevel(idx)}>
								{localize(props.levelSet.levels[idx].title).text}
							</Tabs.Inline.Item>
						))}
					</Tabs.Inline>
					<LevelCriteria mt={2}>{props.data.levels[selectedLevel]}</LevelCriteria>
				</Collapsable>
			</CompetencySuggestionContainer>
		</>
	);
}

const CompetencySuggestionsContainer = styled(Box)`
	max-width: 80%;
`;

function CompetencySuggestions(props: {
	data: graphql.CompetencySuggestion[];
	levelSet: graphql.CompetencyLevelSet;
	onTake: (suggestion: graphql.CompetencySuggestion[]) => void;
}) {
	const { t } = useTranslation();
	const [deselected, setDeselected] = useState<number[]>([]);

	return (
		<>
			<CompetencySuggestionsContainer mb={2}>
				{props.data.map((suggestion, idx) => (
					<CompetencySuggestion
						key={idx}
						data={suggestion}
						levelSet={props.levelSet}
						selected={!deselected.includes(idx)}
						onToggleSelected={() => {
							if (deselected.includes(idx)) {
								setDeselected(deselected.filter((i) => i !== idx));
							} else {
								setDeselected([...deselected, idx]);
							}
						}}
					/>	
				))}
			</CompetencySuggestionsContainer>
			<Flex>
				<Button
					onClick={async () => await props.onTake(props.data.filter((_, idx) => !deselected.includes(idx)))}
					title={t('competencies.magic.take')}
				/>
			</Flex>
		</>
	);
}

const fadeOut = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
	}
`

const LevelSetSelectorContainer = styled(Flex)<{ picked: boolean }>`
	flex-direction: column;
	align-items: center;
	gap: 10px;

	animation-delay: 400ms;
	animation-duration: 200ms;
	animation-timing-function: linear;
	animation-fill-mode: both;
	${(props) => props.picked && css`animation-name: ${fadeOut};`}
`;

export default function JobCompetencyGeneratorModal(props: {
	jobTitle: graphql.JobTitle;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const language = useLanguage();
	const { localize } = useLocalize();
	const [start, { loading: starting }] = graphql.useStartCompetencySuggestionsMutation();
	const [advance, { loading: advancing }] = graphql.useAdvanceCompetencySuggestionsMutation();


	const { data }  = graphql.useCompetencyLevelSetsQuery({ variables: { isArchived: false, first: 10 } });
	const levelSets = (data?.competencyLevelSets.edges ?? []).map(({ node }) => node as graphql.CompetencyLevelSet);
	const [levelSet, setLevelSet] = useState<graphql.CompetencyLevelSet | null>(null);
	const [isSelectingLevelSet, setIsSelectingLevelSet] = useState(true);

	const [create, { loading: createLoading }] = graphql.useCreateCompetencyMutation();
	const [attach, { loading: attachLoading }] = graphql.useAttachJobCompetencyMutation();

	const onTakeSuggestion = async (suggestions) => {
		for (const suggestion of suggestions) {
			const input = {
				competencyLevelSetID: levelSet!.id,
				title: [
					{
						languageID: language.id,
						text: suggestion.title,
					},
				],
				description: [
					{
						languageID: language.id,
						text: {
							ops: [
								{
									insert: suggestion.description,
								},
							],
						} as Delta,
					},
				],
				levels: suggestion.levels.map((criteria, idx) => ({
					competencyLevelID: levelSet!.levels[idx].id,
					enabled: true,
					criteria: [
						{
							languageID: language.id,
							text: {
								ops: [
									{
										insert: criteria,
									},
								],
							} as Delta,
						},
					],
				})),
				jobTitleIDs: [props.jobTitle.id],
				attachableToGoal: true,
			};

			const result = await create({
				variables: {
					input,
				},
			});

			// TODO: Picks 0 level by default, but perhaps we should incorporate selecting levels in the flow?
			await attach({
				variables: {
					input: {
						competencyID: result.data!.createCompetency.competency.id,
						competencyLevel: 0,
						jobTitleID: props.jobTitle.id,
					}
				}
			});
		}

		props.onDismiss();
	};

	return (
		<Popover center backdrop visible>
			<MagicChatModal<graphql.CompetencySuggestion[]>
				translationKey="competencies"
				onDismiss={props.onDismiss}
				startChat={async (reset) => {
					const response = await start({
						variables: {
							input: {
								locale: language.id,
								reset,
								jobTitleID: props.jobTitle.id,
								levelSetID: levelSet!.id,
							},
						},
					});

					return { chat: response.data!.startCompetencySuggestions.chat };
				}}
				starting={starting}
				advanceChat={async (chatID, userResponse) => {
					const response = await advance({
						variables: {
							input: {
								locale: language.id,
								chatID,
								userResponse,
							},
						},
					});
					
					return {
						chat: response.data!.advanceCompetencySuggestions.chat,
						suggestion: response.data!.advanceCompetencySuggestions.suggestion ?? null,
					};
				}}
				advancing={advancing}
				renderSuggestion={(suggestions) => <CompetencySuggestions data={suggestions} levelSet={levelSet!} onTake={onTakeSuggestion} />}
				isPreConversation={isSelectingLevelSet}
				preConversationMessages={[t('competencies.magic.levelSet.prompt')]}
				preConversationAccessory={(
					<LevelSetSelectorContainer my={3} picked={!!levelSet}>
						<Select
							options={levelSets.map((set) => ({
								value: set.id,
								title: localize(set.title).text,
							}))}
							selectedValue={levelSet?.id}
							onChange={(value) => {
								setLevelSet(levelSets.find((set) => set.id === value)!)
								setTimeout(() => setIsSelectingLevelSet(false), 600);
							}}
							placeholder={t('common.settings.competencies.create.selectExisting')}
							search={false}
						/>
						<Paragraph color="slateBlue">
							{t('common.settings.competencies.create.or')}&nbsp;
							<Link to="/settings/competencies/create/levels" style={{ textDecoration: 'underline' }}>
								{t('common.settings.competencies.create.createLevelSet').toLowerCase()}
							</Link>
						</Paragraph>
					</LevelSetSelectorContainer>
				)}
				preConversationResult={levelSet && localize(levelSet.title).text}
				onReset={() => {
					setLevelSet(null);
					setIsSelectingLevelSet(true);
				}}
			/>
		</Popover>
	);
}